import { Dispatch, SetStateAction, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Stores some state in the history.location.state, which is saved throughout navigation.
 * If the parameters are constant, the setState part can be considered stable and this can be used
 * as a drop-in replacement for useState.
 * @param key the key used to store the value in the location state
 * @param defaultValue returned if the location state doesn't have a value yet
 */
const useLocationState = <T>(
  key: string,
  defaultValue: T,
): readonly [T, Dispatch<SetStateAction<T | undefined>>] => {
  const location = useLocation<any>();
  const history = useHistory<any>();
  const setState = useCallback(
    (value: SetStateAction<T | undefined>) => {
      const { location } = history;

      const oldValue = location.state?.[key];
      const newValue =
        typeof value === 'function'
          ? (value as unknown as (v: T) => T)(oldValue === undefined ? defaultValue : oldValue)
          : value;

      // no point setting the default value, when it would be returned anyway
      if (oldValue === undefined && newValue === defaultValue) {
        return;
      }
      // no point changing the location object if value hasn't changed
      if (newValue === oldValue) {
        return;
      }

      history.replace({ ...location, state: { ...location.state, [key]: newValue } });
    },
    [defaultValue, history, key],
  );

  const value: T | undefined = location.state?.[key];
  return [value ?? defaultValue, setState] as const;
};

export default useLocationState;
